<template>
  <section class="payment-schedule" data-cy="payment-schedule">
    <div class="sub-header-text">PAYMENT SCHEDULE</div>
    <div class="payment-schedule__scroll">
    <Table v-if="installments.length">
      <thead>
        <tr>
          <th v-if="!compact" class="col-position">#</th>
          <th class="text-left">Installment</th>
          <th class="text-right">Draw Amount</th>
          <th v-if="!compact" class="text-right">%</th>
          <th v-if="compact" class="text-right">Collected</th>
          <th v-if="compact" class="text-right">Remaining</th>
          <th v-if="!compact" class="text-right">Due Date</th>
          <th class="text-right">Payment Status</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(installment, index) in installments"
          :key="installment.id ?? installment.position"
          :class="rowClassFor(index)"
          data-cy="payment-schedule-row"
        >
          <td v-if="!compact" class="col-position">{{ installment.position + 1 }}</td>
          <td class="text-left">{{ installment.label }}</td>
          <td class="text-right">
            {{ currency(dollarCentsFor(index) / 100) }}
          </td>
          <td v-if="!compact" class="text-right">
            {{ percentageFor(index).toFixed(2) }}%
          </td>
          <td
            v-if="compact"
            class="text-right"
            :data-cy="`payment-schedule-collected-${index}`"
          >
            {{ currency(collectedCentsFor(index) / 100) }}
          </td>
          <td
            v-if="compact"
            class="text-right"
            :data-cy="`payment-schedule-remaining-${index}`"
          >
            {{ currency(remainingCentsFor(index) / 100) }}
          </td>
          <td v-if="!compact" class="text-right">
            {{ formatDueDate(installment.due_date) }}
          </td>
          <td class="text-right">
            <Badge
              :text="statusTextFor(index)"
              :color="statusColorFor(index)"
              variant="chip"
              :data-cy="`payment-schedule-status-${index}`"
            />
          </td>
        </tr>
      </tbody>
    </Table>
    </div>
  </section>
</template>

<script setup lang="ts">
import currency from "serviceshift-ui/shared/src/filters/currency";
import { computed } from "vue";

import Badge from "@/components/Badge.vue";
import Table from "@/serviceshift-ui/components/General/Table.vue";
import type {
  ProgressBillingInstallment,
  ProgressBillingPayload,
  SpbInstallmentStatus
} from "@/typings/invoice";
import type {
  Installment,
  JobPaymentSchedule
} from "@/vuex/modules/jobPaymentSchedule";

const props = withDefaults(
  defineProps<{
    schedule: JobPaymentSchedule;
    paymentContext?: ProgressBillingPayload | null;
    compact?: boolean;
  }>(),
  { paymentContext: null, compact: false }
);

const installments = computed<Installment[]>(
  () => props.schedule?.installments ?? []
);

// Rails serializes decimal columns as strings to preserve precision, so the
// raw fields arrive as strings (e.g. "50.0"). Coerce before arithmetic /
// .toFixed(), or rendering blows up silently in production.
//
// When a `paymentContext` is supplied (Accept Payment modal, invoice
// detail page) we drive Draw Amount and % from the presenter payload so
// the row stays internally consistent with Collected/Remaining — the
// schedule's `calculated_opposite_value` is a snapshot of the dollar
// figure at plan-creation time and can drift from the live contract
// total. With no payload (QuoteBuilder preview), fall back to the
// snapshot.
const dollarCentsForSchedule = (installment: Installment): number =>
  Number(
    props.schedule.split_mode === "amount"
      ? installment.value
      : installment.calculated_opposite_value
  );

const dollarCentsFor = (index: number): number => {
  const payload = payloadFor(index);
  if (payload) return Number(payload.amount_cents ?? 0);
  return dollarCentsForSchedule(installments.value[index]);
};

const percentageFor = (index: number): number => {
  const payload = payloadFor(index);
  if (payload) return Number(payload.percentage ?? 0);
  const installment = installments.value[index];
  return Number(
    props.schedule.split_mode === "percentage"
      ? installment.value
      : installment.calculated_opposite_value
  );
};

// Map backend status enum to the badge text/color the row renders.
const STATUS_TEXT: Record<SpbInstallmentStatus, string> = {
  paid: "Paid",
  partially_paid: "Partially Paid",
  current_draw: "Current Draw",
  past_due: "Past Due",
  scheduled: "Scheduled",
  cancelled: "Cancelled"
};

const STATUS_COLOR: Record<SpbInstallmentStatus, string> = {
  paid: "green",
  partially_paid: "yellow",
  current_draw: "yellow",
  past_due: "red",
  scheduled: "gray",
  cancelled: "gray"
};

// Looks up the per-row payload from the backend by position. When no
// `paymentContext` is supplied (QuoteBuilder modal preview / configure
// modal), this falls back to `null` and the legacy hardcoded behavior
// applies — the first row is current_draw, every other row is scheduled.
const payloadFor = (index: number): ProgressBillingInstallment | null => {
  const ctx = props.paymentContext;
  if (!ctx) return null;
  const inst = installments.value[index];
  return (
    ctx.installments.find(
      (i: ProgressBillingInstallment) => i.position === inst.position
    ) ?? null
  );
};

const statusFor = (index: number): SpbInstallmentStatus | null =>
  payloadFor(index)?.status ?? null;

// Collected / remaining come from the presenter's pool-walk (paid_cents,
// remaining_cents). Without a payment context (preview surfaces), show
// zero collected and the full draw amount remaining.
const collectedCentsFor = (index: number): number =>
  Number(payloadFor(index)?.paid_cents ?? 0);

const remainingCentsFor = (index: number): number => {
  const payload = payloadFor(index);
  if (payload) return Number(payload.remaining_cents ?? 0);
  return dollarCentsForSchedule(installments.value[index]);
};

const statusTextFor = (index: number): string => {
  const status = statusFor(index);
  if (status) return STATUS_TEXT[status];
  return index === 0 ? "Current Draw" : "Scheduled";
};

const statusColorFor = (index: number): string => {
  const status = statusFor(index);
  if (status) return STATUS_COLOR[status];
  return index === 0 ? "yellow" : "gray";
};

const rowClassFor = (index: number): string =>
  statusFor(index) === "past_due" ? "payment-schedule__row--past-due" : "";

// Date-only strings (e.g. "2026-05-15") parse as UTC midnight, so format in
// UTC to avoid the column shifting back a day in negative timezones.
const formatDueDate = (dueDate?: string | null): string => {
  if (!dueDate) return "—";
  const parsed = new Date(dueDate);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC"
  });
};
</script>

<style lang="scss" scoped>
.sub-header-text {
  font-size: 14px;
  font-weight: bolder;
  line-height: 16px;
  margin-bottom: 14px;
}

.payment-schedule {
  margin-top: 16px;

  // 8 columns can overflow narrow containers (Accept Payment modal sits at
  // ~750px). Horizontal scroll keeps the Payment Status column reachable
  // without forcing the table to wrap or shrink columns past readability.
    &__scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    min-width: 0;

    :deep(table.table) {
      width: max-content;
      min-width: 100%;
      overflow: visible;
    }
  }

  .col-position {
    width: 40px;
    text-align: center;
  }

  // Keep all cells on one line so the scroll container, not text wrap,
  // resolves the horizontal-overflow case.
  table {
    white-space: nowrap;
  }

  // Visual emphasis for past-due installment rows (AC6).
  &__row--past-due {
    background-color: rgba(220, 38, 38, 0.06);
    color: var(--sera-color-status-red, #b91c1c);
  }
}
</style>
